/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { ComponentChild } from 'preact';
import { BaseButton } from '../UNSAFE_BaseButton';
import { ButtonLabelLayout } from '../UNSAFE_ButtonLabelLayout';

type AriaProps =
  | {
      /**
       * The ID of an element (or space separated IDs of multiple elements) that
       * describes the button.
       */
      'aria-describedby'?: string;

      /**
       * Whether the button should be hidden from screen readers. When true, no aria-label is required.
       */
      'aria-hidden'?: false;

      /**
       * accessibleLabel - the aria label
       */
      'aria-label': string;
    }
  | {
      'aria-describedby'?: string;

      'aria-hidden': true;

      'aria-label'?: string;
    };

type BaseProps = {
  /**
   * Specifies the icon for the button.
   */
  children?: ComponentChild;

  /**
   * Specifies that the button element should be disabled.
   * @default false
   */
  isDisabled?: boolean;

  /**
   * Triggered when a button is clicked, whether by keyboard, mouse, or touch events.
   */
  onAction?: () => void | null;

  /**
   * The size indicates how large the button is rendered. Note that only the ghost variant supports the xs size.
   * @default 'sm'
   */
  size?: 'xs' | 'sm';
};

type Props = BaseProps & AriaProps;

export const EmbeddedIconButton = ({
  isDisabled = false,
  size = 'sm',
  onAction,
  'aria-label': accessibleLabel,
  'aria-describedby': ariaDescribedBy,
  'aria-hidden': ariaHidden = false,
  ...props
}: Props) => {
  return (
    <BaseButton
      elementDetails={{ type: 'span', isFocusable: false }}
      isDisabled={isDisabled}
      size={size}
      variant={'ghost'}
      styling={['min']}
      onAction={onAction}
      aria-describedby={ariaDescribedBy}
      aria-hidden={ariaHidden}
      aria-label={accessibleLabel}>
      <ButtonLabelLayout
        display={'icons'}
        startIcon={props.children}
        size={size}></ButtonLabelLayout>
    </BaseButton>
  );
};
